import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PropertyComment } from "../types/propertyComment";
import { AppDispatch, RootState } from "../../../store/store";
import BASE_URL from "../api";
import { fetchProperty } from "./propertySlice";

interface CommentState {
  comments: PropertyComment[];
  loading: boolean;
  error: any;
}

const initialState: CommentState = {
  comments: [],
  loading: false,
  error: null,
};

const propertyCommentSlice = createSlice({
  name: "propertyComment",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload;
    },

    fetchCommentsSuccess(state, action: PayloadAction<PropertyComment[]>) {
      state.loading = false;
      state.comments = action.payload;
    },


    addCommentSuccess(state, action: PayloadAction<PropertyComment>) {
      state.loading = false;

      // prevent duplicates (important)
      const exists = state.comments.find(c => c.id === action.payload.id);
      if (!exists) {
        state.comments.unshift(action.payload);
      }
    },

    deleteCommentSuccess(state, action: PayloadAction<number>) {
      state.loading = false;
      state.comments = state.comments.filter(c => c.id !== action.payload);
    },

  },
});

export const {
  start,
  fail,
  fetchCommentsSuccess,
  addCommentSuccess,
  deleteCommentSuccess,
} = propertyCommentSlice.actions;

export default propertyCommentSlice.reducer;



export const fetchComments =
  (propertyId: string) =>
    async (dispatch: AppDispatch) => {
      dispatch(start());

      try {
        const res = await fetch(`${BASE_URL}/property/${propertyId}/comments`);
        const data = await res.json();

        // ✅ NORMALIZE HERE
        const formatted = data.data.map((c: any) => ({
          ...c,
          user: c.user
            ? {
              id: c.user.id,
              name: `${c.user.first_name} ${c.user.last_name}`,
              image: c.user.image,
            }
            : undefined,
        }));

        dispatch(fetchCommentsSuccess(formatted));
      } catch (err) {
        dispatch(fail(err));
      }
    };

export const addComment =
  (body: { property_id: string; rating: number; comment?: string }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;

        const res = await fetch(`${BASE_URL}/user/property/comment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        const data = await res.json();

        if (!res.ok) throw new Error(data?.message);

        dispatch(addCommentSuccess(data.data));

        // ✅  IMPORTANT: REFRESH PROPERTY DETAILS
        dispatch(fetchProperty(body.property_id));

      } catch (err) {
        dispatch(fail(err));
        throw err;
      }
    };

export const deleteComment =
  (id: number) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;

        await fetch(`${BASE_URL}/user/property/comment/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        dispatch(deleteCommentSuccess(id));
      } catch (err) {
        dispatch(fail(err));
      }
    };



export type { CommentState };